import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, ShieldCheck } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/shared";
import { SERVICE_OPTIONS } from "@/data/content";
import { waLink, WHATSAPP_DISPLAY, WHATSAPP_NUMBER } from "@/lib/site";

const INITIAL = {
  nome: "", condominio: "", telefone: "", whatsapp: "", email: "", cidade: "",
  estado: "", cargo: "", tipoImovel: "", servico: "", area: "", horario: "", descricao: "",
  website: "",
};

const ufList = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function QuoteForm() {
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (form.website) return;
    if (!form.nome.trim() || !form.telefone.trim() || !form.servico) {
      setError("Preencha os campos obrigatórios: nome, telefone e serviço desejado.");
      return;
    }
    setError("");
    const lines = [
      "*NOVA SOLICITAÇÃO DE ORÇAMENTO — SITE REALIZA ENGENHARIA*", "",
      `*Nome:* ${form.nome}`,
      form.condominio && `*Condomínio/Empresa:* ${form.condominio}`,
      `*Telefone:* ${form.telefone}`,
      form.whatsapp && `*WhatsApp:* ${form.whatsapp}`,
      form.email && `*E-mail:* ${form.email}`,
      (form.cidade || form.estado) && `*Cidade/UF:* ${form.cidade}${form.estado ? " - " + form.estado : ""}`,
      form.cargo && `*Cargo:* ${form.cargo}`,
      form.tipoImovel && `*Tipo de imóvel:* ${form.tipoImovel}`,
      `*Serviço desejado:* ${form.servico}`,
      form.area && `*Área aproximada:* ${form.area}`,
      form.horario && `*Melhor horário para contato:* ${form.horario}`,
      form.descricao && `*Descrição do problema:* ${form.descricao}`,
    ].filter(Boolean);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
    setSent(true);
    toast.success("Solicitação enviada! Você foi direcionado ao WhatsApp.");
  };

  return (
    <section id="orcamento" className="grain relative scroll-mt-20 overflow-hidden py-24 lg:py-32">
      <div className="pointer-events-none absolute right-0 top-1/3 h-[500px] w-[500px] rounded-full bg-gold/[0.05] blur-[140px]" />
      <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[1fr_1.5fr] lg:px-8">
        <div>
          <SectionHeading
            eyebrow="Orçamento"
            title={<>Solicite uma <span className="text-gold-grad">avaliação técnica</span></>}
            sub="Preencha o formulário e sua solicitação chega diretamente ao nosso WhatsApp com todos os dados. Um engenheiro entra em contato para agendar a avaliação."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-4">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-form-whatsapp-direct"
                className="flex items-center gap-4 border border-gold/25 bg-navy-800/50 p-5 transition-colors duration-300 hover:border-gold/60"
              >
                <MessageCircle className="h-6 w-6 text-gold" />
                <span>
                  <span className="block font-display text-lg font-bold tracking-wide text-white">Prefere falar agora?</span>
                  <span className="block text-xs text-slate-400">WhatsApp direto com a equipe: {WHATSAPP_DISPLAY}</span>
                </span>
              </a>
              <div className="flex items-start gap-3 border border-white/[0.07] bg-white/[0.02] p-5">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <p className="text-xs leading-relaxed text-slate-500">
                  Seus dados são utilizados exclusivamente para o atendimento da sua solicitação,
                  conforme a LGPD. Após o envio, você poderá anexar fotos e documentos
                  diretamente na conversa do WhatsApp.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div className="relative border border-gold/20 bg-navy-800/60 p-6 backdrop-blur-sm sm:p-9" data-testid="quote-form-card">
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="flex min-h-[480px] flex-col items-center justify-center text-center"
                  data-testid="quote-form-success"
                >
                  <span className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-gold bg-gold/10">
                    <CheckCircle2 className="h-10 w-10 text-gold" />
                  </span>
                  <h3 className="mt-7 font-display text-3xl font-extrabold uppercase tracking-wide text-white">
                    Obrigado! Sua solicitação foi recebida.
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">
                    A equipe Realiza Engenharia entrará em contato. Se a janela do WhatsApp não
                    abriu, toque no botão abaixo para enviar a mensagem.
                  </p>
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <a
                      href={waLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="quote-form-reopen-whatsapp"
                      className="rounded-full bg-gold px-8 py-3.5 font-display text-sm font-bold tracking-[0.14em] text-navy-950 transition-colors hover:bg-gold-bright"
                    >
                      ABRIR WHATSAPP
                    </a>
                    <button
                      onClick={() => { setForm(INITIAL); setSent(false); }}
                      data-testid="quote-form-reset"
                      className="rounded-full border border-white/15 px-8 py-3.5 font-display text-sm font-bold tracking-[0.14em] text-slate-300 transition-colors hover:border-gold hover:text-gold"
                    >
                      ENVIAR OUTRA SOLICITAÇÃO
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={submit}
                  noValidate
                  data-testid="quote-form"
                >
                  <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="field-label" htmlFor="qf-nome">Nome *</label>
                      <input id="qf-nome" className="field" placeholder="Seu nome completo" value={form.nome} onChange={set("nome")} data-testid="quote-form-input-nome" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-cond">Condomínio / Empresa</label>
                      <input id="qf-cond" className="field" placeholder="Nome do condomínio ou empresa" value={form.condominio} onChange={set("condominio")} data-testid="quote-form-input-condominio" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-tel">Telefone *</label>
                      <input id="qf-tel" className="field" placeholder="(51) 90000-0000" value={form.telefone} onChange={set("telefone")} data-testid="quote-form-input-telefone" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-wa">WhatsApp</label>
                      <input id="qf-wa" className="field" placeholder="Se houver, informe aqui" value={form.whatsapp} onChange={set("whatsapp")} data-testid="quote-form-input-whatsapp" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-email">E-mail</label>
                      <input id="qf-email" type="email" className="field" placeholder="voce@email.com" value={form.email} onChange={set("email")} data-testid="quote-form-input-email" />
                    </div>
                    <div className="grid grid-cols-[1fr_90px] gap-3">
                      <div>
                        <label className="field-label" htmlFor="qf-cidade">Cidade</label>
                        <input id="qf-cidade" className="field" placeholder="Sua cidade" value={form.cidade} onChange={set("cidade")} data-testid="quote-form-input-cidade" />
                      </div>
                      <div>
                        <label className="field-label" htmlFor="qf-uf">Estado</label>
                        <select id="qf-uf" className="field appearance-none" value={form.estado} onChange={set("estado")} data-testid="quote-form-input-estado">
                          <option value="">UF</option>
                          {ufList.map((uf) => <option key={uf} value={uf} className="bg-navy-900">{uf}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-cargo">Cargo</label>
                      <select id="qf-cargo" className="field appearance-none" value={form.cargo} onChange={set("cargo")} data-testid="quote-form-input-cargo">
                        <option value="">Selecione</option>
                        {["Síndico(a)", "Síndico(a) profissional", "Administradora", "Conselheiro(a)", "Morador(a)", "Proprietário(a)", "Empresa", "Outro"].map((o) => <option key={o} value={o} className="bg-navy-900">{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-tipo">Tipo de imóvel</label>
                      <select id="qf-tipo" className="field appearance-none" value={form.tipoImovel} onChange={set("tipoImovel")} data-testid="quote-form-input-tipo-imovel">
                        <option value="">Selecione</option>
                        {["Condomínio residencial", "Edifício comercial", "Empresa / Indústria", "Escola / Instituição", "Hotel", "Outro"].map((o) => <option key={o} value={o} className="bg-navy-900">{o}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="qf-servico">Serviço desejado *</label>
                      <select id="qf-servico" className="field appearance-none" value={form.servico} onChange={set("servico")} data-testid="quote-form-input-servico">
                        <option value="">Selecione o serviço</option>
                        {SERVICE_OPTIONS.map((o) => <option key={o} value={o} className="bg-navy-900">{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-area">Área aproximada</label>
                      <input id="qf-area" className="field" placeholder="Ex.: 1.200 m²" value={form.area} onChange={set("area")} data-testid="quote-form-input-area" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="qf-horario">Melhor horário para contato</label>
                      <select id="qf-horario" className="field appearance-none" value={form.horario} onChange={set("horario")} data-testid="quote-form-input-horario">
                        <option value="">Selecione</option>
                        {["Manhã", "Tarde", "Noite", "Qualquer horário"].map((o) => <option key={o} value={o} className="bg-navy-900">{o}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="qf-desc">Descrição do problema</label>
                      <textarea id="qf-desc" rows={4} className="field resize-none" placeholder="Conte o que está acontecendo na edificação: infiltrações, fachada desgastada, trincas..." value={form.descricao} onChange={set("descricao")} data-testid="quote-form-input-descricao" />
                    </div>
                  </div>
                  {error && (
                    <p className="mt-4 border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="quote-form-error" role="alert">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="quote-form-submit"
                    className="mt-6 w-full rounded-full bg-gold py-4 font-display text-base font-bold tracking-[0.16em] text-navy-950 transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_36px_rgba(212,175,55,0.35)]"
                  >
                    SOLICITAR ORÇAMENTO
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
